import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import {
  DEMO_ORGANIZATION,
  DEMO_SEAT_USERS,
  AGENCY_ENTERPRISE_PLANS,
  levelColor,
  type CertificationLevel,
  type PricingLicense,
} from "../lib/certifications";
import {
  fetchLicenses,
  assignSeat,
  revokeSeat,
  transferSeat,
  fetchDepartments,
  type DepartmentRecord,
} from "../lib/sentinel-api";
import { LICENSE_TIERS } from "../lib/licensing";

const TIER_LABELS: Record<string, string> = Object.fromEntries(
  LICENSE_TIERS.map((t) => [t.id, t.name]),
);

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

export default function LicensesPage() {
  const [activeLicenseId, setActiveLicenseId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  const [allLicenses, setAllLicenses] = useState<PricingLicense[]>(AGENCY_ENTERPRISE_PLANS.filter((p) => p.type === "agency" || p.type === "enterprise").slice(0, 4));
  const [assignResult, setAssignResult] = useState<string | null>(null);
  const [departments, setDepartments] = useState<DepartmentRecord[]>([]);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");

  useEffect(() => {
    fetchLicenses().then((lics) => {
      if (lics.length) {
        setAllLicenses(lics.filter((p) => p.type === "agency" || p.type === "enterprise").slice(0, 4));
      }
    });
    fetchDepartments(DEMO_ORGANIZATION.id).then((depts) => {
      if (depts.length) setDepartments(depts);
    });
  }, []);

  const orgLicenses = AGENCY_ENTERPRISE_PLANS.filter((plan) =>
    DEMO_ORGANIZATION.seat_plans.includes(plan.id),
  );

  const activeLicense = activeLicenseId ? allLicenses.find((l) => l.id === activeLicenseId) : null;
  const assignedUsers = activeLicenseId ? DEMO_SEAT_USERS : [];

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (assignEmail && activeLicenseId) {
      setAssignResult("Assigning seat...");
      await assignSeat(activeLicenseId, assignEmail, DEMO_ORGANIZATION.id);
      setAssignResult("Seat assigned successfully!");
    }
    setShowAssignModal(false);
    setAssignEmail("");
    setTimeout(() => setAssignResult(null), 3000);
  }

  async function handleRevoke(userId: string) {
    if (!activeLicenseId) return;
    setAssignResult("Revoking seat...");
    await revokeSeat(activeLicenseId, userId);
    setAssignResult("Seat revoked successfully!");
    setTimeout(() => setAssignResult(null), 3000);
  }

  async function handleTransfer(fromUserId: string) {
    if (!activeLicenseId) return;
    const toEmail = prompt("Enter the email of the user to transfer the seat to:");
    if (!toEmail) return;
    setAssignResult("Transferring seat...");
    await transferSeat(activeLicenseId, fromUserId, toEmail, DEMO_ORGANIZATION.id);
    setAssignResult("Seat transferred successfully!");
    setTimeout(() => setAssignResult(null), 3000);
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Licenses & Seat Management</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Manage multi-seat licenses, assign seats to team members, and track usage for {DEMO_ORGANIZATION.name}.
        </p>
      </div>

      {!activeLicense ? (
        <>
          {/* Department overview */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)", flexWrap: "wrap", gap: "0.75rem" }}>
            <h2 style={{ fontSize: "1.125rem", marginBottom: 0 }}>Departments</h2>
            <button onClick={() => setShowDeptModal(true)} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>+ Add Department</button>
          </div>
          {departments.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "0.75rem", marginBottom: "var(--space-5)" }}>
              {departments.map((dept) => {
                const deptLicenses = allLicenses.filter((l) => (l as unknown as { department_id?: string }).department_id === dept.id);
                return (
                  <div key={dept.id} className="card" style={{ padding: "1rem" }}>
                    <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{dept.name}</h4>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {deptLicenses.length} license{deptLicenses.length !== 1 ? "s" : ""} assigned
                    </div>
                    {dept.budget != null && (
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                        Budget: ${dept.budget.toLocaleString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-5)" }}>
              No departments yet. Create one to organize licenses by team.
            </p>
          )}

          {/* License list */}
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Active Licenses</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {allLicenses.map((license) => {
              const assigned = DEMO_ORGANIZATION.assigned_seats[license.id] || [];
              const seatsUsed = assigned.length || DEMO_SEAT_USERS.length;
              return (
                <div key={license.id} className="card" style={{ padding: "var(--space-4)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div>
                      <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{license.name}</h3>
                      <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>{license.industry}</div>
                      <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                        {license.levels_covered.map((lvl) => (
                          <LevelPill key={lvl} level={lvl} />
                        ))}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>{seatsUsed} / {license.seats}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>seats used</div>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.375rem",
                          padding: "0.25rem 0.625rem",
                          borderRadius: "var(--radius-full)",
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          background: "var(--accent-50)",
                          color: "var(--accent-600)",
                        }}
                      >
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--accent-500)" }} />
                        {license.status}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginTop: "0.75rem" }}>
                    <div style={{ width: "100%", height: "6px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                      <div style={{ width: `${(seatsUsed / license.seats) * 100}%`, height: "100%", background: "linear-gradient(90deg, var(--accent-500), var(--accent-400))", borderRadius: "var(--radius-full)" }} />
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.75rem" }}>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      Term: {license.start_date} to {license.end_date}
                    </div>
                    <button
                      onClick={() => setActiveLicenseId(license.id)}
                      className="btn btn-primary"
                      style={{ fontSize: "0.8125rem" }}
                    >
                      Manage Seats
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {/* Seat management view */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
            <div>
              <button
                onClick={() => setActiveLicenseId(null)}
                style={{ background: "transparent", border: "none", color: "var(--accent-600)", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", marginBottom: "0.25rem" }}
              >
                &larr; Back to Licenses
              </button>
              <h2 style={{ fontSize: "1.25rem" }}>{activeLicense.name}</h2>
            </div>
            <button
              onClick={() => setShowAssignModal(true)}
              className="btn btn-primary"
              style={{ fontSize: "0.8125rem" }}
            >
              + Assign Seat
            </button>
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: "var(--space-4)" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Name</th>
                    <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Email</th>
                    <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Role</th>
                    <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Level</th>
                    <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Status</th>
                    <th style={{ textAlign: "right", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedUsers.map((user) => (
                    <tr key={user.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", fontWeight: 600 }}>{user.name}</td>
                      <td style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{user.email}</td>
                      <td style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)", textTransform: "capitalize" }}>{user.role.replace("_", " ")}</td>
                      <td style={{ padding: "0.75rem 1rem" }}><LevelPill level={user.level} /></td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <span
                          style={{
                            padding: "0.125rem 0.5rem",
                            borderRadius: "var(--radius-full)",
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            background: user.status === "active" ? "var(--accent-50)" : "var(--warning-50)",
                            color: user.status === "active" ? "var(--accent-600)" : "var(--warning-600)",
                          }}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td style={{ padding: "0.75rem 1rem", textAlign: "right" }}>
                        <button onClick={() => handleTransfer(user.id)} style={{ background: "transparent", border: "none", color: "var(--primary-600)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", marginRight: "0.5rem" }}>Transfer</button>
                        <button onClick={() => handleRevoke(user.id)} style={{ background: "transparent", border: "none", color: "var(--error-600)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>Revoke</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* License details */}
          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>License Details</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}>
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Term Start</div>
                <div style={{ fontSize: "0.875rem" }}>{activeLicense.start_date}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Term End</div>
                <div style={{ fontSize: "0.875rem" }}>{activeLicense.end_date}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Renewal Status</div>
                <div style={{ fontSize: "0.875rem", color: "var(--accent-600)", fontWeight: 600 }}>Active</div>
              </div>
            </div>
            <div style={{ marginTop: "0.75rem" }}>
              <Link to="/renewals" className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>View Renewal Options</Link>
            </div>
          </div>
        </>
      )}

      {/* Add department modal */}
      {showDeptModal && (
        <div
          onClick={() => setShowDeptModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 200,
            padding: "var(--space-4)",
          }}
        >
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: "var(--space-5)", maxWidth: "440px", width: "100%" }}>
            <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>Add Department</h3>
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
              Create a new department to organize licenses and seat assignments.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); if (newDeptName) { setDepartments((prev) => [...prev, { id: `dept-${Date.now()}`, organization_id: DEMO_ORGANIZATION.id, name: newDeptName, budget: null, created_by: null, admin_user_id: null, created_at: new Date().toISOString() }]); setShowDeptModal(false); setNewDeptName(""); } }}>
              <div className="form-group">
                <label className="form-label" htmlFor="dept-name">Department Name</label>
                <input id="dept-name" type="text" className="form-input" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} placeholder="e.g. Field Operations" />
              </div>
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "var(--space-3)" }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Create Department</button>
                <button type="button" onClick={() => setShowDeptModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign seat modal */}
      {showAssignModal && (
        <div
          onClick={() => setShowAssignModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 200,
            padding: "var(--space-4)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ padding: "var(--space-5)", maxWidth: "440px", width: "100%" }}
          >
            <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>Assign Seat</h3>
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
              Search for an existing member by email, or invite a new user to your organization.
            </p>
            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label className="form-label" htmlFor="assign-email">Member Email</label>
                <input
                  id="assign-email"
                  type="email"
                  className="form-input"
                  value={assignEmail}
                  onChange={(e) => setAssignEmail(e.target.value)}
                  placeholder="name@organization.gov"
                />
              </div>
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "var(--space-3)" }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Assign & Invite</button>
                <button type="button" onClick={() => setShowAssignModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PlatformLayout>
  );
}
